import { json } from 'aurelia-fetch-client';
import { autoinject } from 'aurelia-framework';
import { ApiService } from '../../../utils/api-service';
import { MunicipalityService, CustomerMunicipality } from "../../municipalities/municipality-service";
import { isInteger } from 'lodash';
import { IText } from '../../../utils/translator';
import { DataItemSelectionResult, IServiceDataItems } from '../../customers/reported-service-data-item-service';
import { AttachedDataItem } from '../../reported-service-data-items/reported-service-data-item-models';

export interface IMeasureAM {
	id: string;
	code: string;
	name: string;
	specifier: string;
	unit: string;
}

export interface IMeasureSummaryAM {
	id: string;
	code: string;
	title: string;
}

export class Measure {
	id: string;
	name: IText;
}

export interface IMeasureInputAM {
	measureId: string;
	measureName: string;
	measureUnit: string;
	measureSpecifier: string;
	specifier: string;
	serviceId: string;
	isForCustomerComparison: boolean;
	forServiceTotal: boolean;
	forServiceByMunicipalities: boolean;
	forCostCentersTotal: boolean;
	forCostCentersByMunicipalities: boolean;
	allowDecimals: boolean;
	isImported: boolean;
	minValue: number;
	maxValue: number;
	existingEntries: IMeasureEntryAM[];
	description: string;
	ruleReferences: IMeasureInputRuleReferenceAM[];
}

export interface IMeasureInputRuleReferenceAM {
	costCenterId: string;
	dimensionValueId: string;
	description: string;
}

export interface IMeasureEntryAM {
	costCenterId: string;
	customerMunicipalityId: string;
	measureId: string;
	scopeId: string;
	serviceId: string;
	status: string;
	value: number;
	dimensionValueIds: string[];
	isCollected: boolean;
	hasRuleReferences: boolean;
}

export interface IMeasureAM {
	id: string;
	name: string;
	unit: string;
	specifier: string;
	description: string;
}

export interface IValidationError {
	resourceKey: string;
	params: any[];
}

export class MeasureEntryAM implements IMeasureEntryAM {
	id: string;
	customerId: string;
	costCenterId: string;
	customerMunicipalityId: string;
	measureId: string;
	scopeId: string;
	serviceId: string;
	status: string;
	value: any;
	allowDecimals: boolean;
	validationError: IValidationError;
	minValue: number;
	maxValue: number;
	dimensionValueIds: string[] = [];
	description: string;
	isCollected: boolean;
	hasRuleReferences: boolean;

	validationResultOk: boolean;

	constructor(init?: Partial<IMeasureInputAM>) {
		Object.assign(this, init);
	}

	static getInstance(init: IMeasureEntryAM) : MeasureEntryAM {
		var entry = new MeasureEntryAM();
		Object.assign(entry, init);
		return  entry;
	}

	isMinValueValid(val: number): boolean {
		if (typeof this.minValue === 'number') {
			return val >= this.minValue;
		}
		return true;
	}

	isMaxValueValid(val: number): boolean {
		if (typeof this.maxValue === 'number') {
			return val <= this.maxValue;
		}
		return true;
	}

	isEntryMeasured(measureInput: IMeasureInputAM) : boolean {

		if(this.costCenterId && this.customerMunicipalityId && !measureInput.forCostCentersByMunicipalities) { return false };
		if(this.costCenterId && !this.customerMunicipalityId && !measureInput.forCostCentersTotal) { return false };

		if(!this.costCenterId && this.customerMunicipalityId && !measureInput.forServiceByMunicipalities) { return false };
		if(!this.costCenterId && !this.customerMunicipalityId && !measureInput.forServiceTotal) { return false };

		return true;
	}
	
	validateAndNormalize(): void {

		const normalizedValue = this.normalize(this.value);

		let validationError = this.getValidationError(this.value, normalizedValue);
		this.validationResultOk = validationError == null;
		this.validationError = validationError;
		if (this.validationResultOk) {
			this.value = normalizedValue;
		}
	}

	normalize(value: any): number {
		const normalizedValue = (value != undefined && value !== '')
			? parseFloat(value.toString().replace(',', '.').replace(/\s/g, ''))
			: null;
		return normalizedValue;
	}

	private getValidationError(originalValue: string, normalizedValue: number): IValidationError {

		if (!originalValue || originalValue === '') {
			return null;
		}
		if (isNaN(normalizedValue)) {
			return <IValidationError>{
				resourceKey: this.allowDecimals
					? 'Measures_ValidationError_MeasureInput_OnlyNumbersAllowed'
					: 'Measures_ValidationError_MeasureInput_OnlyIntegersAllowed'
			}
		}
		if (!this.allowDecimals && !isInteger(normalizedValue)) {
			return <IValidationError>{
				resourceKey: 'Measures_ValidationError_MeasureInput_OnlyIntegersAllowed'
			}
		}
		if (!this.isMinValueValid(normalizedValue)) {
			return <IValidationError>{
				resourceKey: 'Measures_ValidationError_MeasureInput_ValueLessThanMinValue',
				params: [this.minValue]
			}
		}
		if (!this.isMaxValueValid(normalizedValue)) {
			return <IValidationError>{
				resourceKey: 'Measures_ValidationError_MeasureInput_ValueGreaterThanMaxValue',
				params: [this.maxValue]
			}
		}
		return null;
	}
}

export abstract class ServiceDataItemHandler {
	attachDataItem: (dataItem: DataItemSelectionResult) => Promise<any>;
	setInputTypeState: (dataItem: AttachedDataItem, type: string, isCollected: boolean) => Promise<any>;
	detachDataItem: (dataItem: AttachedDataItem) => Promise<any>;
	changeDescription: (dataItem: AttachedDataItem) => Promise<any>;
	changeCustomerComparisonState: (dataItem: AttachedDataItem) => Promise<any>;

	protected createServiceDataItems(serviceDataAM: IServiceDataItems): IServiceDataItems {
		return Object.keys(serviceDataAM).reduce((item, key) => {
			item[key] = serviceDataAM[key].map(item => new AttachedDataItem(item));
			return item;
		}, {});
	}
}

export interface IMissingMeasureEntriesAM {
	costCenterIds: string[];
	serviceIds: string[];
}

@autoinject
export class MeasureService {

	constructor(private http: ApiService, private municipalityService: MunicipalityService) { }

	getMeasureInputs(
		scopeId: string,
		serviceId: string,
		costCenterId: string = null,
		dimensionValueIds: string[] = null): Promise<IMeasureInputAM[]> {

		let url = `measure-entries/in-scope/${scopeId}/for-service/${serviceId}`;
		if (costCenterId) {
			url = `${url}/for-costcenter/${costCenterId}`;
		}
		if (dimensionValueIds && !!dimensionValueIds.length) {
			const dimensionValueIdFragment = dimensionValueIds.map(id => `dimensionValueIds=${id}`).join('&');
			url = `${url}?${dimensionValueIdFragment}`;
		}
		return this.http.fetchJson(url);
	}

	async getMeasureInputsForServices(scopeId: string, serviceIds: string[]): Promise<IMeasureInputAM[]> {
		const res = await this.http.fetch(`measure-entries/in-scope/${scopeId}/for-services`, {
			method: 'post',
			body: json(serviceIds)
		});
		return res.json();
	}

	getMeasures() : Promise<IMeasureAM[]> {
		return this.http.fetchJson('measures');
	}

	async getMeasuresForFilters(): Promise<IMeasureSummaryAM[]> {
		return this.http.fetchJson('measures/for-filters');
	}

	getServicesMissingMeasures(scopeId: string): Promise<IMissingMeasureEntriesAM> {
		return this.http.fetchJson(`measure-entries/get/${scopeId}/entries-missing`);
	}

	insertMeasureValue(measureEntry: IMeasureEntryAM, customerId: string): Promise<any> {
		const dto = {
			measureId: measureEntry.measureId,
			scopeId: measureEntry.scopeId,
			value: isNaN(measureEntry.value) ? null : measureEntry.value,
			serviceId: measureEntry.serviceId,
			costCenterId: measureEntry.costCenterId,
			customerMunicipalityId: measureEntry.customerMunicipalityId,
			dimensionValueIds: measureEntry.dimensionValueIds,
			hasRuleReferences: measureEntry.hasRuleReferences,
		};

		return this.http.fetch(`measure-entries/put/${measureEntry.serviceId}/${customerId}`, {
			method: 'put',
			body: json(dto)
		});
	}

	copyMeasureEntriesToScope(serviceId: string, customerId: string, targetScopeId: string, sourceScopeId: string, costCenterId: string) : Promise<any> {
		return this.http.fetch(`measure-entries/for-service/${serviceId}/of-scope/${sourceScopeId}/copy-to/?targetScopeId=${targetScopeId}&costCenterId=${costCenterId}`,
		{
			method: 'post'
		});
	}	
}
